import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader, Card, Badge, EmptyState } from "@/components/Primitives";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Pencil, Trash2, Building2, ListChecks, SlidersHorizontal, Sparkles } from "lucide-react";
import { toast } from "sonner";

const EVAL_TYPES = [
  { v: "yes_no", l: "Yes / No" },
  { v: "rating", l: "Rating 1-5" },
  { v: "score", l: "Score" },
  { v: "sla", l: "SLA-based" },
];

export default function Settings() {
  const { departments, refreshDepartments, settings, refreshSettings } = useApp();

  // department dialog
  const [deptOpen, setDeptOpen] = useState(false);
  const [deptForm, setDeptForm] = useState({ name: "", description: "", active: true });
  const [deptEditId, setDeptEditId] = useState(null);

  // criteria
  const [critDept, setCritDept] = useState("");
  const [criteria, setCriteria] = useState([]);
  const [critOpen, setCritOpen] = useState(false);
  const [critForm, setCritForm] = useState({ name: "", description: "", eval_type: "yes_no", active: true });
  const [critEditId, setCritEditId] = useState(null);

  // thresholds
  const [thresh, setThresh] = useState({ low_performer_threshold: 60, sla_hours: 24, notify_overdue_days: 30 });

  const loadCriteria = useCallback((deptId) => {
    if (!deptId) return;
    api.get("/criteria", { params: { department_id: deptId } }).then((r) => setCriteria(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!critDept && departments[0]) setCritDept(departments[0].id);
  }, [departments, critDept]);
  useEffect(() => { loadCriteria(critDept); }, [critDept, loadCriteria]);
  useEffect(() => { if (settings) setThresh({ low_performer_threshold: settings.low_performer_threshold, sla_hours: settings.sla_hours, notify_overdue_days: settings.notify_overdue_days }); }, [settings]);

  // ---- Department CRUD ----
  const saveDept = async () => {
    if (!deptForm.name) { toast.error("Name required."); return; }
    try {
      if (deptEditId) await api.put(`/departments/${deptEditId}`, deptForm);
      else await api.post("/departments", deptForm);
      toast.success("Department saved.");
      setDeptOpen(false); refreshDepartments();
    } catch (e) { toast.error("Failed."); }
  };
  const delDept = async (id) => {
    if (!window.confirm("Delete department and all its criteria?")) return;
    await api.delete(`/departments/${id}`); toast.success("Department deleted."); refreshDepartments();
    if (critDept === id) setCritDept("");
  };

  // ---- Criteria CRUD ----
  const saveCrit = async () => {
    if (!critForm.name || !critDept) { toast.error("Name & department required."); return; }
    const payload = { ...critForm, department_id: critDept, order: criteria.length };
    try {
      if (critEditId) await api.put(`/criteria/${critEditId}`, payload);
      else await api.post("/criteria", payload);
      toast.success("Criterion saved.");
      setCritOpen(false); loadCriteria(critDept);
    } catch (e) { toast.error("Failed."); }
  };
  const delCrit = async (id) => {
    if (!window.confirm("Delete this criterion? It will be removed from all readiness checklists.")) return;
    await api.delete(`/criteria/${id}`); toast.success("Deleted."); loadCriteria(critDept);
  };
  const toggleCritActive = async (c) => {
    await api.put(`/criteria/${c.id}`, { ...c, active: !c.active });
    loadCriteria(critDept);
  };

  const saveThresh = async () => {
    await api.put("/settings", {
      low_performer_threshold: Number(thresh.low_performer_threshold),
      sla_hours: Number(thresh.sla_hours),
      notify_overdue_days: Number(thresh.notify_overdue_days),
    });
    toast.success("Thresholds updated."); refreshSettings();
  };

  const seed = async () => {
    const r = await api.post("/seed");
    toast.success(r.data.message || "Seeded.");
    refreshDepartments();
  };

  return (
    <div>
      <PageHeader
        title="Settings"
        subtitle="Everything is configurable — no developer required."
        action={<Button data-testid="seed-button" onClick={seed} variant="outline" className="border-slate-700 gap-2"><Sparkles className="h-4 w-4 text-[#C9A24B]" /> Load Demo Data</Button>}
      />

      <Tabs defaultValue="departments">
        <TabsList className="bg-[#1E293B] border border-slate-800">
          <TabsTrigger value="departments" data-testid="tab-departments"><Building2 className="h-4 w-4 mr-2" />Departments</TabsTrigger>
          <TabsTrigger value="criteria" data-testid="tab-criteria"><ListChecks className="h-4 w-4 mr-2" />Training Criteria</TabsTrigger>
          <TabsTrigger value="thresholds" data-testid="tab-thresholds"><SlidersHorizontal className="h-4 w-4 mr-2" />Thresholds</TabsTrigger>
        </TabsList>

        {/* Departments */}
        <TabsContent value="departments" className="mt-6">
          <div className="flex justify-end mb-4">
            <Button data-testid="add-department-button" onClick={() => { setDeptForm({ name: "", description: "", active: true }); setDeptEditId(null); setDeptOpen(true); }} className="bg-[#C9A24B] text-[#0B1120] hover:bg-[#b8923f] gap-2"><Plus className="h-4 w-4" />Add Department</Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {departments.length === 0 && <Card className="md:col-span-3"><EmptyState icon={Building2} title="No departments" hint="Add a department or load demo data." /></Card>}
            {departments.map((d) => (
              <Card key={d.id} className="p-5" data-testid={`dept-card-${d.id}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-white font-medium">{d.name}</p>
                    <p className="text-slate-500 text-sm mt-1">{d.description || "—"}</p>
                  </div>
                  <Badge tone={d.active ? "green" : "slate"}>{d.active ? "Active" : "Inactive"}</Badge>
                </div>
                <div className="flex gap-2 mt-4">
                  <button onClick={() => { setDeptForm(d); setDeptEditId(d.id); setDeptOpen(true); }} className="text-slate-400 hover:text-[#C9A24B]" data-testid={`edit-dept-${d.id}`}><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => delDept(d.id)} className="text-slate-400 hover:text-[#DC2626]" data-testid={`delete-dept-${d.id}`}><Trash2 className="h-4 w-4" /></button>
                </div>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Criteria */}
        <TabsContent value="criteria" className="mt-6">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <Select value={critDept} onValueChange={setCritDept}>
              <SelectTrigger data-testid="criteria-dept-select" className="w-[220px] border-slate-700"><SelectValue placeholder="Select department" /></SelectTrigger>
              <SelectContent>{departments.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
            </Select>
            <Button data-testid="add-criterion-button" disabled={!critDept} onClick={() => { setCritForm({ name: "", description: "", eval_type: "yes_no", active: true }); setCritEditId(null); setCritOpen(true); }} className="bg-[#C9A24B] text-[#0B1120] hover:bg-[#b8923f] gap-2"><Plus className="h-4 w-4" />Add Criterion</Button>
          </div>
          <Card className="p-2">
            {criteria.length === 0 ? (
              <EmptyState icon={ListChecks} title="No criteria" hint="Add training criteria that drive this department's readiness checklist." />
            ) : (
              <div className="divide-y divide-slate-800">
                {criteria.map((c) => (
                  <div key={c.id} className="flex items-center justify-between p-4" data-testid={`criterion-row-${c.id}`}>
                    <div className="flex-1">
                      <p className={`text-sm ${c.active ? "text-white" : "text-slate-500 line-through"}`}>{c.name}</p>
                      {c.description && <p className="text-slate-500 text-xs mt-0.5">{c.description}</p>}
                    </div>
                    <div className="flex items-center gap-4">
                      <Badge tone="slate">{EVAL_TYPES.find((e) => e.v === c.eval_type)?.l}</Badge>
                      <Switch checked={c.active} onCheckedChange={() => toggleCritActive(c)} data-testid={`toggle-criterion-${c.id}`} />
                      <button onClick={() => { setCritForm(c); setCritEditId(c.id); setCritOpen(true); }} className="text-slate-400 hover:text-[#C9A24B]" data-testid={`edit-criterion-${c.id}`}><Pencil className="h-4 w-4" /></button>
                      <button onClick={() => delCrit(c.id)} className="text-slate-400 hover:text-[#DC2626]" data-testid={`delete-criterion-${c.id}`}><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>

        {/* Thresholds */}
        <TabsContent value="thresholds" className="mt-6">
          <Card className="p-6 max-w-lg">
            <div className="space-y-5">
              <div>
                <Label>Low-Performer Flag Threshold (%)</Label>
                <Input type="number" value={thresh.low_performer_threshold} onChange={(e) => setThresh({ ...thresh, low_performer_threshold: e.target.value })} className="border-slate-700 mt-1" data-testid="threshold-input" />
                <p className="text-slate-500 text-xs mt-1">Members below this readiness % are flagged for coaching.</p>
              </div>
              <div>
                <Label>SLA Window (hours)</Label>
                <Input type="number" value={thresh.sla_hours} onChange={(e) => setThresh({ ...thresh, sla_hours: e.target.value })} className="border-slate-700 mt-1" />
                <p className="text-slate-500 text-xs mt-1">e.g. missed calls must be followed up within X hours.</p>
              </div>
              <div>
                <Label>Overdue Coaching Alert (days)</Label>
                <Input type="number" value={thresh.notify_overdue_days} onChange={(e) => setThresh({ ...thresh, notify_overdue_days: e.target.value })} className="border-slate-700 mt-1" />
                <p className="text-slate-500 text-xs mt-1">Flag members without a session in this many days.</p>
              </div>
              <Button data-testid="save-thresholds-button" onClick={saveThresh} className="bg-[#C9A24B] text-[#0B1120] hover:bg-[#b8923f]">Save Thresholds</Button>
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Department dialog */}
      <Dialog open={deptOpen} onOpenChange={setDeptOpen}>
        <DialogContent className="bg-[#0F172A] border-slate-800">
          <DialogHeader><DialogTitle className="font-display">{deptEditId ? "Edit Department" : "Add Department"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Name</Label><Input value={deptForm.name} onChange={(e) => setDeptForm({ ...deptForm, name: e.target.value })} className="border-slate-700" data-testid="dept-name-input" /></div>
            <div><Label>Description</Label><Textarea value={deptForm.description} onChange={(e) => setDeptForm({ ...deptForm, description: e.target.value })} className="border-slate-700" /></div>
            <div className="flex items-center justify-between"><Label>Active</Label><Switch checked={deptForm.active} onCheckedChange={(v) => setDeptForm({ ...deptForm, active: v })} /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeptOpen(false)}>Cancel</Button>
            <Button data-testid="save-dept-button" onClick={saveDept} className="bg-[#C9A24B] text-[#0B1120] hover:bg-[#b8923f]">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Criterion dialog */}
      <Dialog open={critOpen} onOpenChange={setCritOpen}>
        <DialogContent className="bg-[#0F172A] border-slate-800">
          <DialogHeader><DialogTitle className="font-display">{critEditId ? "Edit Criterion" : "Add Training Criterion"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Name</Label><Input value={critForm.name} onChange={(e) => setCritForm({ ...critForm, name: e.target.value })} className="border-slate-700" data-testid="criterion-name-input" placeholder="e.g. Sales pitch matches approved script" /></div>
            <div><Label>Description</Label><Textarea value={critForm.description} onChange={(e) => setCritForm({ ...critForm, description: e.target.value })} className="border-slate-700" /></div>
            <div>
              <Label>Evaluation Type</Label>
              <Select value={critForm.eval_type} onValueChange={(v) => setCritForm({ ...critForm, eval_type: v })}>
                <SelectTrigger className="border-slate-700" data-testid="criterion-eval-type"><SelectValue /></SelectTrigger>
                <SelectContent>{EVAL_TYPES.map((e) => <SelectItem key={e.v} value={e.v}>{e.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between"><Label>Active</Label><Switch checked={critForm.active} onCheckedChange={(v) => setCritForm({ ...critForm, active: v })} /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCritOpen(false)}>Cancel</Button>
            <Button data-testid="save-criterion-button" onClick={saveCrit} className="bg-[#C9A24B] text-[#0B1120] hover:bg-[#b8923f]">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
